import { useCallback } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  SquarePenIcon,
  ToggleLeftIcon,
  ToggleRightIcon,
  Trash2Icon,
} from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import { MODAL_DELETE, MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useBrains from '@/hooks/useBrains';
import useDialogs from '@/hooks/useDialogs';
import useWebhooks from '@/hooks/useWebhooks';
import { popModal, pushModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { Webhook } from '@/shared/types/webhook';
import { walkAuthSubAgentConfigs } from '@/util/auth/walkAuthSubAgentConfigs';

interface ActionsCellProps {
  webhook: Webhook;
  brainId: string;
  canWrite: boolean;
}

export const ActionsCell = ({ webhook, brainId, canWrite }: ActionsCellProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const slug = useSelector(selectAccountSlug);
  const { deleteWebhook, updateWebhook } = useWebhooks(brainId);
  const { findConnectedDialogsForWebhooks, dialogs } = useDialogs(brainId);
  const { isWebhookUsedInAuth } = useBrains(brainId);

  const handleRefClick = useCallback(
    (url: string) => {
      dispatch(popModal());
      // Auth refs encode `auth:<dialog_id>:<action_id>` so the warning link
      // deep-links to the specific sub_agent action (selected via ?action=).
      if (url.startsWith('auth:')) {
        const [, dialogId, actionId] = url.split(':');
        if (dialogId) {
          navigate(
            `/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialogId}?action=${actionId}`
          );
        }
        return;
      }
      navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${url}`);
    },
    [brainId, dispatch, navigate, slug]
  );

  const handleEdit = useCallback(() => {
    trackEvent(EventName.ClickEditWebhook, { webhook_id: webhook.webhook_id });
    navigate(`/${slug}/ai-agents/${brainId}/workflows/webhooks/${webhook.webhook_id}`);
  }, [webhook.webhook_id, navigate, slug, brainId]);

  const handleExport = useCallback(() => {
    trackEvent(EventName.ClickExportWebhook, { webhook_id: webhook.webhook_id });

    // Strip system-generated IDs and set enabled to false
    const {
      webhook_id: _webhook_id,
      brain_id: _brain_id,
      created: _created,
      updated: _updated,
      ...exportData
    } = webhook;
    const sanitizedWebhook = { ...exportData, enabled: false };

    const webhookData = JSON.stringify(sanitizedWebhook, null, 2);
    const blob = new Blob([webhookData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `webhook-${webhook.name.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [webhook]);

  const handleToggleEnabled = useCallback(() => {
    updateWebhook({ brain_id: brainId, webhook_id: webhook.webhook_id, enabled: !webhook.enabled });
  }, [brainId, webhook.webhook_id, webhook.enabled, updateWebhook]);

  const handleDelete = useCallback(() => {
    trackEvent(EventName.ClickDeleteWebhook, { webhook_id: webhook.webhook_id });

    const usedDialogs = findConnectedDialogsForWebhooks(webhook.webhook_id);
    const usedInAuth = isWebhookUsedInAuth(webhook.webhook_id);
    const authRefs = usedInAuth
      ? walkAuthSubAgentConfigs(dialogs).map(({ location }) => ({
          url: `auth:${location.dialog_id}:${location.action_id}`,
          label: t('webhook.delete_warning_used_in_auth_link'),
        }))
      : [];
    const references = [...usedDialogs, ...authRefs];

    if (!isEmpty(references)) {
      const warning = (
        <AlertWithReferences
          references={references}
          onRefClick={handleRefClick}
          referenceProp="url"
        >
          <Trans i18nKey="webhook.delete_warning" values={[webhook.name]} />
        </AlertWithReferences>
      );
      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else {
      const deleteProps = {
        title: t('webhook.delete_webhook'),
        subtitle: <Trans i18nKey="webhook.delete_warning_with_name" values={[webhook.name]} />,
        onDelete: () => {
          deleteWebhook(webhook.webhook_id);
        },
      };
      dispatch(pushModal(MODAL_DELETE, deleteProps));
    }
  }, [
    webhook.webhook_id,
    webhook.name,
    findConnectedDialogsForWebhooks,
    isWebhookUsedInAuth,
    dialogs,
    handleRefClick,
    t,
    dispatch,
    deleteWebhook,
  ]);

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          disabled={!canWrite}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem onSelect={handleEdit} className="flex items-center gap-2">
          <SquarePenIcon size={16} className="text-muted-foreground" />
          {t('common.edit')}
        </DropdownMenuItem>

        <DropdownMenuItem onSelect={handleExport} className="flex items-center gap-2">
          <DownloadIcon size={16} className="text-muted-foreground" />
          {t('common.export')}
        </DropdownMenuItem>

        {['pre_message', 'post_message', 'first_message', 'pre_nba'].includes(webhook.type) && (
          <DropdownMenuItem onSelect={handleToggleEnabled} className="flex items-center gap-2">
            {webhook.enabled ? (
              <ToggleLeftIcon size={16} className="text-muted-foreground" />
            ) : (
              <ToggleRightIcon size={16} className="text-muted-foreground" />
            )}
            {webhook.enabled ? t('common.deactivate') : t('common.activate')}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onSelect={handleDelete}
          variant="destructive"
          className="flex items-center gap-2"
        >
          <Trash2Icon size={16} />
          {t('common.delete')}…
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
